import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EyeIcon, EyeOffIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAcceptInvite } from '@/hooks/use-auth';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { logAuthStage } from '@/lib/auth-log';
import { passwordSchema, PASSWORD_HINT } from '@/lib/password-policy';

const formSchema = z.object({
    // Shared account password policy (see @/lib/password-policy).
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

export default function AcceptInvitePage() {
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { handleAuthError } = useErrorHandler();

    // Accept invite mutation
    const acceptInviteMutation = useAcceptInvite();

    // Get token and email from URL parameters
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    // Redirect if token or email is missing
    useEffect(() => {
        if (!token || !email) {
            toast({
                title: 'Invalid Invitation Link',
                description: 'This invitation link is invalid or has expired.',
                variant: 'destructive',
            });
            navigate('/login');
        }
    }, [token, email, navigate, toast]);

    // Clear error when user starts typing
    const clearError = () => {
        if (submitError) {
            setSubmitError(null);
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (!token || !email) return;

        try {
            setSubmitError(null);

            // Prepare the payload
            const payload = {
                token,
                email: decodeURIComponent(email),
                password: data.password,
                confirmPassword: data.confirmPassword,
            };

            logAuthStage('accept-invite-submit');

            // Call the accept invite API using the hook
            await acceptInviteMutation.mutateAsync(payload);

            toast({
                title: 'Welcome to LarkPM! 🎉',
                description: 'Your account has been created successfully. Please log in with your new password.',
                variant: 'success',
            });

            // Redirect to login page
            navigate('/login');

        } catch (error: any) {
            console.error('Accept invite error:', error);
            setSubmitError(error.message || 'Failed to accept invitation. Please try again.');
            handleAuthError(error);
        }
    };

    // Show loading state if we don't have required params yet
    if (!token || !email) {
        return (
            <div className="min-h-screen bg-muted flex items-center justify-center p-6">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-border border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-muted-foreground">Validating invitation...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center p-6">
            <div className="w-full max-w-4xl">
                <Card className="border-border rounded-2xl overflow-hidden">
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[600px]">
                            {/* Left side - Image */}
                            <div className="relative p-6">
                                <img
                                    src="/login.jpg"
                                    alt="Accept Invitation"
                                    className="w-full h-full object-cover rounded-xl scale-x-[-1]"
                                />
                            </div>

                            {/* Right side - Accept Invitation Form */}
                            <div className="px-8 py-12 flex flex-col justify-center">
                                {/* Logo */}
                                <div className="mb-6">
                                    <img
                                        src="/logo.png"
                                        alt="LarkPM Logo"
                                        className="h-10 w-auto object-contain"
                                    />
                                </div>

                                {/* Header */}
                                <div className="mb-6">
                                    <h1 className="text-xl font-bold tracking-tight text-foreground mb-1">
                                        Accept Invitation 🎉
                                    </h1>
                                    <p className="text-muted-foreground text-sm">
                                        You've been invited to join LarkPM! Create your password to get started.
                                    </p>
                                    <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                                        <p className="text-sm text-blue-700 font-medium">
                                            Inviting: {decodeURIComponent(email)}
                                        </p>
                                    </div>
                                </div>

                                <Form {...form}>
                                    {/* Error Alert */}
                                    {submitError && (
                                        <Alert className="mb-4 bg-red-50 border-red-200">
                                            <InfoIcon className="h-4 w-4 text-red-600" />
                                            <AlertTitle className="text-red-800">
                                                Failed to accept invitation
                                            </AlertTitle>
                                            <AlertDescription className="text-red-700">
                                                {submitError}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    <form
                                        onSubmit={form.handleSubmit(onSubmit)}
                                        className="space-y-4"
                                    >
                                        {/* Password */}
                                        <FormField
                                            control={form.control}
                                            name="password"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-foreground font-medium">
                                                        Create Password<span className="text-red-600">*</span>
                                                    </FormLabel>
                                                    <FormControl>
                                                        <div className="relative">
                                                            <Input
                                                                type={showPassword ? 'text' : 'password'}
                                                                placeholder="Enter your password"
                                                                {...field}
                                                                className="pr-10"
                                                                onChange={(e) => {
                                                                    field.onChange(e);
                                                                    clearError();
                                                                }}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                aria-label={
                                                                    showPassword ? 'Hide password' : 'Show password'
                                                                }
                                                            >
                                                                {showPassword ? (
                                                                    <EyeOffIcon className="h-4 w-4" />
                                                                ) : (
                                                                    <EyeIcon className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        {PASSWORD_HINT}
                                                    </div>
                                                </FormItem>
                                            )}
                                        />

                                        {/* Confirm Password */}
                                        <FormField
                                            control={form.control}
                                            name="confirmPassword"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-foreground font-medium">
                                                        Confirm Password<span className="text-red-600">*</span>
                                                    </FormLabel>
                                                    <FormControl>
                                                        <div className="relative">
                                                            <Input
                                                                type={showConfirmPassword ? 'text' : 'password'}
                                                                placeholder="Confirm your password"
                                                                {...field}
                                                                className="pr-10"
                                                                onChange={(e) => {
                                                                    field.onChange(e);
                                                                    clearError();
                                                                }}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                aria-label={
                                                                    showConfirmPassword ? 'Hide password' : 'Show password'
                                                                }
                                                            >
                                                                {showConfirmPassword ? (
                                                                    <EyeOffIcon className="h-4 w-4" />
                                                                ) : (
                                                                    <EyeIcon className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {/* Create Password Button */}
                                        <div className="mt-6">
                                            <Button
                                                type="submit"
                                                className="w-full bg-foreground-soft hover:bg-foreground text-primary-foreground disabled:opacity-50 h-11"
                                                disabled={acceptInviteMutation.isPending}
                                            >
                                                {acceptInviteMutation.isPending ? (
                                                    <span className="flex items-center">
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                        Creating Account...
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center">
                                                        Create Password
                                                        <CheckIcon className="h-4 w-4 ml-2" />
                                                    </span>
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </Form>

                                {/* Login Link */}
                                <div className="mt-6 text-center text-sm">
                                    <span className="text-muted-foreground">Already have an account? </span>
                                    <Button
                                        type="button"
                                        variant="link"
                                        onClick={() => navigate('/login')}
                                        className="text-primary hover:underline p-0 h-auto text-sm"
                                    >
                                        Login here
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
